import is from '@sindresorhus/is';
import { first } from 'lodash';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { StoreApi } from 'zustand';

import type {
    Competencies,
    JobProfileComponent,
    JobProfileLegacy,
    SectionTagDefinition,
} from '~/shared/data-contract/job-profile';
import type { JobProfileVersion, JobProfileVersions } from '~/shared/data-contract/job-profile-versions';
import type { JobProfileSkill } from '~/shared/data-contract/skill';
import { HTTPError } from '~/shared/http-fetcher/http-error';

import { stripCommentMarks } from '../../comments';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { useCompetencies } from '../api/use-competencies';
import type { JobItemDetails } from '../api/use-job-item-details';
import { useJobItemVersions } from '../api/use-job-item-versions';
import { useUpdateJobProfile } from '../api/use-update-job-profile';
import { useTrackSkillChanges } from '../hooks/analytics/use-track-skill-changes';
import { useTrackVersionSaved } from '../hooks/analytics/use-track-version-saved';
import { reconcileTasksBlockWithLatestRefs } from '../profile-tasks/draft/profile-task-save-lifecycle';
import { useProfileTaskFormActions } from '../profile-tasks/use-profile-task-form-actions';
import { useActiveLanguage } from '../providers/ActiveLanguageProvider';
import { normalizeJobProfile } from '../utils/normalize-job-profile';
import type { DraftVersion, DraftVersionStore } from './draft-version-store';
import { useReportMissingSkillLevels } from './skills/use-report-missing-skill-levels';
import { addSkillsToCompetencies, WITHOUT_COMPETENCY_ID } from './skills/use-skills-with-compentencies';
import type { ProfileForm } from './types';

type UseJobProfileFormActionsParams = {
    readonly architectureId: string;
    readonly draftStore: StoreApi<DraftVersionStore>;
    readonly formMethods: UseFormReturn<ProfileForm>;
    readonly hasDraft: boolean;
    readonly item: JobItemDetails;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly parentRoleId?: string;
    readonly profile: JobProfileComponent;
    readonly regeneratedSectionTags?: readonly SectionTagDefinition[];
    readonly resetSkillActionStore: () => void;
    readonly selectedVersion: string | undefined;
    readonly setActiveVersion: (versionId: string) => void;
    readonly setIsEditing: (editing: boolean) => void;
};

export type SaveJobProfileOptions = { readonly withAutoTranslation: boolean };

const normalizeSkillCompetencyId = (skill: JobProfileSkill): JobProfileSkill => ({
    ...skill,
    competencyId:
        skill.skillType === 'language' || skill.competencyId === WITHOUT_COMPETENCY_ID ? undefined : skill.competencyId,
});

type UseJobProfileFormActionsResult = {
    readonly allCompetencies: Competencies | undefined;
    readonly formSkills: readonly JobProfileSkill[];
    readonly handleNewVersion: () => void;
    readonly handleSaveSubmit: (data: ProfileForm, options: SaveJobProfileOptions) => Promise<void>;
    readonly handleSaveValidationError: () => void;
    readonly versions: JobProfileVersions | undefined;
};

const stripStructuredCommentMarks = (
    structured: JobProfileComponent['structured'],
): JobProfileComponent['structured'] =>
    structured?.map((structuredItem) => ({
        ...structuredItem,
        value: stripCommentMarks(structuredItem.value),
    }));

const stripTranslationCommentMarks = (
    translations: JobProfileLegacy['translations'],
): JobProfileLegacy['translations'] =>
    translations.map((translation) => ({
        ...translation,
        structured: stripStructuredCommentMarks(translation.structured),
    }));

type PreparedProfileFormSave = {
    readonly normalizedCompetencies: ReturnType<typeof addSkillsToCompetencies>;
    readonly normalizedSkills: readonly JobProfileSkill[];
    readonly sanitizedProfile: JobProfileComponent;
    readonly sanitizedTranslations: JobProfileLegacy['translations'];
    readonly versionComment?: string;
};

const prepareProfileFormSave = (
    { competencies, translations, versionNote }: ProfileForm,
    profile: JobProfileComponent,
): PreparedProfileFormSave => {
    const normalizedSkills = competencies.flatMap(({ skills }) => skills).map(normalizeSkillCompetencyId);
    const trimmedNote = versionNote?.trim();

    return {
        normalizedCompetencies: addSkillsToCompetencies(competencies, normalizedSkills),
        normalizedSkills,
        sanitizedProfile: {
            ...profile,
            structured: stripStructuredCommentMarks(profile.structured),
            translations: stripTranslationCommentMarks(profile.translations),
        },
        sanitizedTranslations: stripTranslationCommentMarks(translations),
        versionComment: is.nonEmptyString(trimmedNote) ? trimmedNote : undefined,
    };
};

type SaveTaskFetchers = Pick<
    ReturnType<typeof useApplicationFetchers>,
    'readJobLevelDetails' | 'readJobRoleDetails' | 'removeProfileTask'
>;

/**
 * Commits the drafted task removals one by one, recording each id in `completedRemovalIds` so a
 * later failure can reconcile the draft. A 404 means the task already left the item (e.g. through
 * a previously half-applied save), which is exactly the state the removal wants to reach.
 */
const removeCommittedProfileTasks = async ({
    architectureId,
    completedRemovalIds,
    itemId,
    removedTaskIds,
    removeProfileTask,
}: {
    readonly architectureId: string;
    readonly completedRemovalIds: string[];
    readonly itemId: string;
    readonly removedTaskIds: readonly string[];
    readonly removeProfileTask: SaveTaskFetchers['removeProfileTask'];
}): Promise<void> => {
    for (const taskId of removedTaskIds) {
        const removal = await removeProfileTask({ architectureId, itemId, taskId });
        const alreadyGone =
            removal.isErr && removal.error instanceof HTTPError && removal.error.response.status === 404;
        if (removal.isErr && !alreadyGone) {
            throw removal.error;
        }
        completedRemovalIds.push(taskId);
    }
};

/** Reads the task ids referenced by the item's latest version, the set the tasks block must mirror. */
const readLatestTaskRefIds = async ({
    architectureId,
    itemId,
    itemType,
    readJobLevelDetails,
    readJobRoleDetails,
}: {
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly readJobLevelDetails: SaveTaskFetchers['readJobLevelDetails'];
    readonly readJobRoleDetails: SaveTaskFetchers['readJobRoleDetails'];
}): Promise<readonly string[]> => {
    const latestResult =
        itemType === 'job-role'
            ? await readJobRoleDetails({ jobArchitectureId: architectureId, jobRoleId: itemId, version: undefined })
            : await readJobLevelDetails({ jobArchitectureId: architectureId, jobLevelId: itemId, version: undefined });
    if (latestResult.isErr) {
        throw latestResult.error;
    }
    return latestResult.value.jobProfile?.tasks?.map(({ taskId }) => taskId) ?? [];
};

export const useJobProfileFormActions = ({
    architectureId,
    draftStore,
    formMethods,
    hasDraft,
    item,
    itemId,
    itemType,
    parentRoleId,
    profile,
    regeneratedSectionTags,
    resetSkillActionStore,
    selectedVersion,
    setActiveVersion,
    setIsEditing,
}: UseJobProfileFormActionsParams): UseJobProfileFormActionsResult => {
    const { activeLanguage, activeLanguageIndex } = useActiveLanguage();

    const { mutateAsync: updateJobProfile } = useUpdateJobProfile();
    const { readJobLevelDetails, readJobRoleDetails, removeProfileTask } = useApplicationFetchers();
    const { data: versions, refetch: refetchVersions } = useJobItemVersions({ architectureId, itemId, itemType });
    const { data: allCompetencies } = useCompetencies();
    const { enqueueSnackbar } = useSnackbar();
    const formSkills = useWatch({ control: formMethods.control, name: 'skills' });
    const { captureInitialSkills, trackSkillChanges } = useTrackSkillChanges({ architectureId });
    const { setIsNewVersionSave, trackVersionSaved } = useTrackVersionSaved({ architectureId });
    const handleSaveValidationError = useReportMissingSkillLevels(formMethods);
    const { beginSave: beginProfileTaskSave } = useProfileTaskFormActions({
        architectureId,
        itemId,
        itemType,
        itemVersion: item.versionInfo?.version,
        parentRoleId,
        selectedVersion,
    });

    const handleNewVersion = (): void => {
        setIsNewVersionSave(true);
        setIsEditing(true);

        if (versions === undefined) {
            return;
        }

        if (selectedVersion === undefined) {
            return;
        }

        const nextVersion = (first(versions)?.version ?? 0) + 1;
        const draftVersion: DraftVersion = {
            ...item,
            versionInfo: {
                createdAt: new Date().toISOString(),
                isPublished: false,
                isReviewed: false,
                version: nextVersion,
            },
        };

        draftStore.getState().addDraft(draftVersion);

        captureInitialSkills(profile.skills ?? []);
        formMethods.reset({
            competencies: addSkillsToCompetencies([...(profile.competencies?.values() ?? [])], profile.skills ?? []),
            skills: profile.skills ?? [],
            translations: profile.translations,
            versionNote: undefined,
        });
    };

    const finalizeSuccessfulSave = useCallback(
        ({
            latestVersion,
            normalizedCompetencies,
            normalizedSkills,
            sanitizedTranslations,
        }: Pick<PreparedProfileFormSave, 'normalizedCompetencies' | 'normalizedSkills' | 'sanitizedTranslations'> & {
            readonly latestVersion: JobProfileVersion | undefined;
        }): void => {
            if (latestVersion !== undefined) {
                setActiveVersion(latestVersion.version.toString());
            }
            trackVersionSaved({ item, itemId, itemType, latestVersion, parentRoleId });
            trackSkillChanges(normalizedSkills, {
                itemId,
                itemType,
                parentRoleId,
                versionId: latestVersion?.itemId ?? itemId,
            });
            if (hasDraft) {
                draftStore.getState().removeDraft(itemId);
            }
            formMethods.reset({
                competencies: normalizedCompetencies,
                skills: [...normalizedSkills],
                translations: sanitizedTranslations,
                versionNote: undefined,
            });
            setIsEditing(false);
            resetSkillActionStore();
        },
        [
            draftStore,
            formMethods,
            hasDraft,
            item,
            itemId,
            itemType,
            parentRoleId,
            resetSkillActionStore,
            setActiveVersion,
            setIsEditing,
            trackSkillChanges,
            trackVersionSaved,
        ],
    );

    const handleSaveSubmit = useCallback(
        async (form: ProfileForm, { withAutoTranslation }: SaveJobProfileOptions): Promise<void> => {
            const preparedForm = prepareProfileFormSave(form, profile);
            const taskSave = beginProfileTaskSave();
            if (!taskSave.canProceed) {
                enqueueSnackbar(taskSave.blockedMessage, { variant: 'warning' });
                return;
            }

            const completedRemovalIds: string[] = [];
            try {
                await removeCommittedProfileTasks({
                    architectureId,
                    completedRemovalIds,
                    itemId,
                    removedTaskIds: taskSave.removedTaskIds,
                    removeProfileTask,
                });
                // Task generation repins the item outside this edit session, so the block must
                // mirror the latest refs, not the version the session was opened on.
                const tasks =
                    taskSave.tasks === undefined
                        ? undefined
                        : reconcileTasksBlockWithLatestRefs(
                              taskSave.tasks,
                              await readLatestTaskRefIds({
                                  architectureId,
                                  itemId,
                                  itemType,
                                  readJobLevelDetails,
                                  readJobRoleDetails,
                              }),
                          );
                const updatedProfile = await updateJobProfile({
                    acceptLanguage: activeLanguage,
                    architectureId,
                    jobItemType: {
                        id: itemId,
                        type: itemType,
                    },
                    jobProfile: normalizeJobProfile(
                        {
                            ...preparedForm.sanitizedProfile,
                            sectionTags: is.undefined(regeneratedSectionTags)
                                ? profile.sectionTags
                                : [...regeneratedSectionTags],
                            translations: preparedForm.sanitizedTranslations.filter(
                                (_, index) => index === activeLanguageIndex,
                            ),
                        },
                        activeLanguage,
                        preparedForm.normalizedSkills,
                    ),
                    tasks,
                    tasksBaseVersion: Number(selectedVersion ?? item.versionInfo?.version ?? 1),
                    version: selectedVersion,
                    versionComment: preparedForm.versionComment,
                    withAutoTranslation,
                });
                const updatedVersions = await refetchVersions();
                const latestVersion = first(updatedVersions.data);
                taskSave.reportSuccess(updatedProfile.tasks, latestVersion);
                finalizeSuccessfulSave({ ...preparedForm, latestVersion });
            } catch (error: unknown) {
                taskSave.reportFailure(completedRemovalIds);
                enqueueSnackbar((error as { message: string }).message, { variant: 'error' });
            }
        },
        [
            activeLanguage,
            activeLanguageIndex,
            architectureId,
            beginProfileTaskSave,
            enqueueSnackbar,
            itemId,
            readJobLevelDetails,
            readJobRoleDetails,
            removeProfileTask,
            itemType,
            finalizeSuccessfulSave,
            profile,
            refetchVersions,
            regeneratedSectionTags,
            selectedVersion,
            updateJobProfile,
        ],
    );

    return {
        allCompetencies,
        formSkills,
        handleNewVersion,
        handleSaveSubmit,
        handleSaveValidationError,
        versions,
    };
};
